import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {Button, Dialog, Layout, ListItem, Select, Tabs, Text, TextArea, TextField} from '@phonectas/unifon-ui-kit';
import UnifonStarIcon from '../icons/UnifonStarIcon';
import TextWithLabel from '../TextWithLabel';
import {isEmailValid} from 'utils/email';
import useThreeSixtyNewStore from 'common/stores/threeSixtyStore';
import {shallow} from 'zustand/shallow';
import {isValidPhone} from 'utils/validators';
import {phoneNumberWithoutSpaces} from 'utils/number';

interface TrustpilotDialogProps {
    onClose: () => void;
    show: boolean;
    blankData?: boolean;
	phone?: number;
}

export interface TemplateType {
    receiver_mail: string;
    consumer_name: string;
    account_id: string;
    user_id?: string;
}

const TrustpilotDialog: React.FC<TrustpilotDialogProps> = ({
	onClose,
	show,
	blankData = true,
	phone,
}) => {
	const [t] = useTranslation();
	const {
		sendTrustPilotReview,
		getAccountFeed,
		getConfigTemplates,
		sendSms,
	} = useThreeSixtyNewStore((state) => ({
		sendTrustPilotReview: state.sendTrustPilotReview,
		getAccountFeed: state.getAccountFeed,
		getConfigTemplates: state.getConfigTemplates,
		sendSms: state.sendSms,
	}), shallow);
	const {
		user,
		account,
		trustpilotdata,
		caller,
		callerData,
		eventFilter,
	} = useThreeSixtyNewStore((state) => ({
		user: state.activeRow,
		account: state.activeAccount,
		trustpilotdata: state.smsTrustpilotDetails,
		caller: state.bnRedirect.user?.phone_number,
		callerData: state.bnRedirect.user,
		eventFilter: state?.pageFilters?.eventFeed,
	}), shallow);
	const [activeTab, setActiveTab] = useState('1');
	const handleTabChange = useCallback((tab: string | number) => {
		setActiveTab(String(tab));
	}, []);
	const [smsTemplate, setSmsTemplate] = useState('');
	const [smsLanguage, setSmsLanguage] = useState<'NO' | 'EN'>('NO');
	const [phoneNumber, setPhoneNumber] = useState<string | ''>('');
	const tabs = useMemo(() => [
		{value: '1', label: t('Mail')},
		{value: '2', label: t('SMS')},
	], [t]);
	const defaultTemplate: TemplateType = useMemo(() => {
		if (blankData) {
			const callerName = callerData ? callerData?.name : '';
			const callerEmail = callerData ? callerData?.email : '';
			const userId = callerData ? callerData?.id : '';
			return {
				receiver_mail: callerEmail || '',
				consumer_name: callerName || '',
				account_id: account?.id || '',
				user_id: userId || '',
			};
		}
	
		return {
			receiver_mail: user?.email || '',
			consumer_name: user?.name || '',
			account_id: account?.id || '',
			user_id: user?.id || '',
		};
	}, [user, account, blankData]);
	const [temp, setTemp] = useState<TemplateType>(defaultTemplate);
	const onInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		setTemp((prev) => ({
			...prev,
			[e.target.ariaLabel as keyof TemplateType]: e.target.value,
		}));
	}, []);

	const handleNumberChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		setPhoneNumber(e.target.value);
	}, []);
	const onReset = useCallback(() => {
		setTemp((prev) => ({
			...prev,
			receiver_mail: '',
			consumer_name: '',
			user_id: '',
		}));
		setPhoneNumber('');
    	setSmsTemplate('');
    	setSmsLanguage('NO');
	}, []);

	const handleSend = useCallback(() => {
		if (activeTab === '2') {
			sendSms({
				number: phoneNumber,
				text: smsTemplate,
				onSuccess: () => {
					setPhoneNumber('');
					setSmsTemplate('');
					setSmsLanguage('NO');
					onClose();
				}
			});
		}
		if (activeTab === '1') {
			sendTrustPilotReview({ 
				data: temp, 
				onSuccess: () => {
					if (account?.id) {
						getAccountFeed({id: account.id, feedType: eventFilter ?? []});
					}
					onClose();
				},
			});
		}
	}, [
		temp,
		account?.id,
		sendTrustPilotReview,
		getAccountFeed,
		activeTab,
		smsTemplate,
		sendSms,
		phoneNumber,
		onClose,
		eventFilter,
		getAccountFeed,
	]);
	const disableCanSend = useCallback(() => {
		if (activeTab === '1') {
			return !(
				isEmailValid(temp.receiver_mail) &&
				temp.consumer_name &&
				temp.account_id
			);
		}
		if (activeTab === '2') {
			return !(
				isValidPhone(phoneNumber) &&
            smsTemplate &&
            account?.id
			);
		}
		return true;
	}, [activeTab, temp, phoneNumber, smsTemplate, account?.id]);
	const validateEmail = useCallback((value: string | number): boolean => !value || isEmailValid(value as string), []);

	const handleSmsLanguageChange = useCallback((val: string) => {
		setSmsLanguage(val as 'NO' | 'EN');
	},[]);

	const isValidPhoneNumber = useCallback((val: string | number) => {
		if (typeof val === 'number') val = val.toString();
		return val !== '' && isValidPhone(val);
	}, []);

	useEffect(() => {
		if (activeTab === '2' && trustpilotdata === null) {
			getConfigTemplates('trustpilot_sms');
		}
	}, [activeTab, account?.id, getConfigTemplates, trustpilotdata]);

	useEffect(() => {
		if (activeTab === '2' && trustpilotdata) {
			const templates = Array.isArray(trustpilotdata) ? trustpilotdata : [trustpilotdata];
			const langObj = templates[0]?.lang?.[0];
			const newTemplate = smsLanguage === 'NO' ? langObj?.no || '' : langObj?.en || '';
			setSmsTemplate(newTemplate);
			if (caller) {
				const callerNumber = caller ? String(caller) : '';
				setPhoneNumber(phoneNumberWithoutSpaces(callerNumber));
			}
			if (phone && !caller) {
				const phoneNumber = phone ? String(phone) : '';
				setPhoneNumber(phoneNumberWithoutSpaces(phoneNumber));
			}
		}
	}, [smsLanguage, trustpilotdata, activeTab, caller, phone]);

	useEffect(() => {
		if (account?.id) {
			setTemp((prev) => ({
				...prev,
				account_id: account?.id,
			}));
		}
	}, [account?.id]);

	return (
		<Dialog
			id="trustpilot-dialog"
			onOk={handleSend}
			show={show}
			title={
				<Layout alignItems="center" gap="xs">
					<Text variant="h1">{t('Trustpilot')}</Text>
					<UnifonStarIcon />
				</Layout>
			}
			onClose={onClose}
			disableOk={disableCanSend()}
			okButtonLabel={t('Send')}
			closeButtonLabel={t('Close')}
			customActions={
				<Layout fullWidth alignItems="center" justifyContent="space-between" nowrap>
					<Layout alignItems="center">
						<Button onClick={handleSend} variant="contained" color="success" disabled={disableCanSend()}>
							{t('Send')}
						</Button>
						<Button onClick={onClose} compact color="secondary">
							{t('Close')}
						</Button>
					</Layout>
					<Button onClick={onReset} variant="secondary" color="primary">
						{t('Clear')}
					</Button>
				</Layout>
			}
		>
			<>
				<Tabs
					items={tabs}
					active={activeTab}
					onChange={handleTabChange}
				/>
				{activeTab === '1' && (
					<Layout direction="column" gap="md">
						<Layout direction="column" style={{width: '380px'}} gap="sm">
							<TextWithLabel label={t('Customer')} text={account?.account_details?.name || ''} />
							<Layout direction="row" fullWidth gap="sm">
								<TextField
									aria-label="receiver_mail"
									value={temp.receiver_mail}
									label={t('Email')}
									onChange={onInputChange}
									fullWidth
									validator={validateEmail}
									errorMessagePlacement="none"
									inputProps={{autoFocus: true}}
								/>
							</Layout>
							<Layout direction="row" fullWidth gap="sm">
								<TextField
									aria-label="consumer_name"
									value={temp.consumer_name}
									label={t('Name')}
									onChange={onInputChange}
									fullWidth
									validator={(val) => val !== ''}
									errorMessagePlacement="none"
								/>
							</Layout>
						</Layout>
					</Layout>
				)}
				{activeTab === '2' && (
					<Layout direction="column" gap="md">
						<Layout direction="column" style={{width: '380px'}} gap="sm">
							<TextWithLabel label={t('Customer')} text={account?.account_details?.name || ''} />
							<Layout direction="row" fullWidth gap="sm">
								<TextField
									aria-label="receiver_number"
									value={phoneNumber}
									label={t('Phone')}
									onChange={handleNumberChange}
									fullWidth
									validator={isValidPhoneNumber}
									errorMessagePlacement="none"
								/>
							</Layout>
							<Layout direction="row" fullWidth gap="sm">
								<Select
									aria-label="templateSMS"
									value={smsLanguage}
									label={t('Language')}
									onChange={(val) => {handleSmsLanguageChange(val as string)}}
									fullWidth
								>
									<ListItem key="no" value="NO" primaryText="NO" />
									<ListItem key="en" value="EN" primaryText="EN" />
								</Select>
							</Layout>
							<Layout direction="row" fullWidth gap="sm">
								<TextArea
									aria-label="templateSMS"
									value={smsTemplate}
									label={t('Text')}
									onChange={(e) => setSmsTemplate(e.target.value)}
									fullWidth
									style={{height: '200px'}}
								/>
							</Layout>
						</Layout>
					</Layout>
				)}
			</>
		</Dialog>
	);
}

export default TrustpilotDialog;
